import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { sendToWeb3Forms } from "@/lib/web3forms";
import { isValidContact, looksLikeEmail, PROPERTY_TYPES } from "@/lib/formHelpers";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { kontakt: "", property_type: "" };

const CARD =
  "bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(15,92,105,0.35)] ring-1 ring-black/5";

const InquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.property_type) {
      setErr("Vyberte prosím typ prostoru.");
      return;
    }
    if (!isValidContact(form.kontakt)) {
      setErr("Zadejte prosím telefon nebo e-mail.");
      return;
    }
    setState("submitting");
    trackEvent("cta_click_ziskat_nabidku", { location: "hero_form" });
    try {
      const isEmail = looksLikeEmail(form.kontakt);
      const [emailRes, dbRes] = await Promise.all([
        sendToWeb3Forms("Nová poptávka z webu — Hero", {
          "Kontakt": form.kontakt,
          email: isEmail ? form.kontakt : "",
          telefon: isEmail ? "" : form.kontakt,
          "Typ prostoru": form.property_type,
          "Zdroj": "Hero formulář (rychlá nabídka)",
        }),
        axios
          .post(`${API}/inquiries`, {
            name: "Web poptávka — Hero",
            phone: isEmail ? "" : form.kontakt,
            email: isEmail ? form.kontakt : "",
            property_type: form.property_type,
            size: "",
            message: "",
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error("[InquiryForm] DB save failed:", e?.response?.data || e?.message || e);
            return null;
          }),
      ]);
      if (emailRes.success) {
        trackEvent("form_submit_hero", {
          property_type: form.property_type,
          db_saved: dbRes ? "yes" : "no",
        });
        setState("success");
        setForm(initial);
      } else {
        setState("error");
        setErr(
          "Odeslání se nezdařilo. Zkuste to prosím znovu nebo zavolejte na +420 775 601 254."
        );
      }
    } catch {
      setState("error");
      setErr(
        "Odeslání se nezdařilo. Zkuste to prosím znovu nebo zavolejte na +420 775 601 254."
      );
    }
  };

  if (state === "success") {
    return (
      <div data-testid="inquiry-success" className={`${CARD} p-8 md:p-10`}>
        <CheckCircle2 size={40} strokeWidth={1.5} className="text-[#0F5C69]" />
        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-5 text-[#111111]">
          Děkujeme. Ozveme se do 24 hodin.
        </h3>
        <p className="mt-3 text-[#555555] text-sm leading-relaxed">
          Pokud to spěchá, zavolejte nám rovnou na{" "}
          <a
            href="tel:+420775601254"
            className="font-semibold text-[#0F5C69] underline"
          >
            +420 775 601 254
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setState("idle")}
          data-testid="inquiry-reset"
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0F5C69] hover:gap-3 transition-all"
        >
          Získat další nabídku <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  const fieldCls =
    "w-full bg-transparent border-b border-[#D9D9D9] focus:border-[#0F5C69] text-[#111111] placeholder:text-[#999999] py-3 text-sm focus:outline-none transition-colors";
  const labelCls =
    "text-[11px] uppercase tracking-[0.2em] font-bold text-[#555555]";

  return (
    <form
      onSubmit={submit}
      data-testid="inquiry-form"
      className={`${CARD} p-6 md:p-8`}
    >
      <div className="mb-6">
        <h3 className="font-display text-xl md:text-2xl font-bold text-[#111111] tracking-tight">
          Nezávazná kalkulace úklidu
        </h3>
        <p className="mt-2 text-[13px] text-[#666666]">
          Stačí jeden kontakt – ozveme se do 24 hodin.
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <label className={labelCls}>Kontakt *</label>
          <input
            data-testid="form-kontakt"
            type="text"
            inputMode="email"
            required
            value={form.kontakt}
            onChange={update("kontakt")}
            placeholder="+420… nebo vy@firma.cz"
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>Typ prostoru *</label>
          <select
            data-testid="form-property"
            required
            value={form.property_type}
            onChange={update("property_type")}
            className={`${fieldCls} appearance-none cursor-pointer`}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230F5C69' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 2px center",
            }}
          >
            <option value="" disabled>
              Vyberte...
            </option>
            {PROPERTY_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>

      {err && (
        <div
          data-testid="form-error"
          className="mt-5 text-sm text-[#8B1E1E] bg-[#FDECEC] border border-[#F3C6C6] px-4 py-3 rounded-md"
        >
          {err}
        </div>
      )}

      <button
        type="submit"
        data-testid="form-submit"
        disabled={state === "submitting"}
        className="mt-7 group w-full inline-flex items-center justify-center gap-3 bg-[#0F5C69] text-white h-14 font-semibold tracking-tight rounded-lg hover:bg-[#0B4B55] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {state === "submitting" ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Odesílám...
          </>
        ) : (
          <>
            Získat nabídku
            <ArrowRight
              size={18}
              strokeWidth={2.5}
              className="transition-transform group-hover:translate-x-1"
            />
          </>
        )}
      </button>

      <ul
        data-testid="form-benefits"
        className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[#666666]"
      >
        <li className="flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-[#0F5C69]" />
          Ozveme se do 24 hodin
        </li>
        <li className="flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-[#0F5C69]" />
          Bez závazků
        </li>
        <li className="flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-[#0F5C69]" />
          Individuální nabídka
        </li>
      </ul>
    </form>
  );
};

export default InquiryForm;
